import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Row } from 'reactstrap';
import { get } from 'lodash';
import { HiOutlineShoppingCart } from 'react-icons/hi';
import { LuUsers } from 'react-icons/lu';
import { GrGroup } from 'react-icons/gr';
import { FaCarSide, FaWifi } from 'react-icons/fa';
import DetailedSnapshotCard from './DetailedSnapshotCard';
import BasicSnapshotCard from './BasicSnapshotCard';
import { fetchSnapshotStatistics } from '../DashboardActions';
import { getDateDaysAgo, getTodayDate } from '../../common/utils/date/Date.utils';
import ComponentCard from '../../common/components/cards/ComponentCard';
import { deltaGrowth, mapDelta } from '../utils/delta';

function SnapshotOverviewSection() {
  const dispatch = useDispatch();
  const snapshotStatistics = useSelector((state) => state.dashboard.get('snapshotStatistics'));

  const today = getTodayDate();
  const yesterday = getDateDaysAgo(1);
  const sevenDaysAgo = getDateDaysAgo(7);

  const [dataInitialized, setDataInitialized] = useState(false);

  const [newBuyersRangeStart, setNewBuyersRangeStart] = useState(today);
  const [newBuyersRangeEnd, setNewBuyersRangeEnd] = useState(today);
  const [newBuyersCompareStart, setNewBuyersCompareStart] = useState(yesterday);
  const [newBuyersCompareEnd, setNewBuyersCompareEnd] = useState(yesterday);

  const [newSellersRangeStart, setNewSellersRangeStart] = useState(today);
  const [newSellersRangeEnd, setNewSellersRangeEnd] = useState(today);
  const [newSellersCompareStart, setNewSellersCompareStart] = useState(yesterday);
  const [newSellersCompareEnd, setNewSellersCompareEnd] = useState(yesterday);

  const [boughtVehiclesRangeStart, setBoughtVehiclesRangeStart] = useState(sevenDaysAgo);
  const [boughtVehiclesRangeEnd, setBoughtVehiclesRangeEnd] = useState(today);

  useEffect(() => {
    if (!dataInitialized) {
      setDataInitialized(true);
      return;
    }
    dispatch(
      fetchSnapshotStatistics({
        newBuyersRangeStart,
        newBuyersRangeEnd,
        newBuyersCompareStart,
        newBuyersCompareEnd,
        newSellersRangeStart,
        newSellersRangeEnd,
        newSellersCompareStart,
        newSellersCompareEnd,
        boughtVehiclesRangeStart,
        boughtVehiclesRangeEnd,
      }),
    );
  }, [
    newBuyersRangeStart,
    newBuyersRangeEnd,
    newBuyersCompareStart,
    newBuyersCompareEnd,
    newSellersRangeStart,
    newSellersRangeEnd,
    newSellersCompareStart,
    newSellersCompareEnd,
    boughtVehiclesRangeStart,
    boughtVehiclesRangeEnd,
  ]);

  return (
    <ComponentCard title="Snapshot Overview" isCollapsible>
      <Row className="mt-2 dashboard__snapshot-basic-row">
        <BasicSnapshotCard
          title="Total buyers"
          value={get(snapshotStatistics, 'totalBuyers', 0)}
          icon={<HiOutlineShoppingCart />}
          variant="success"
        />
        <BasicSnapshotCard
          title="Total sellers"
          value={get(snapshotStatistics, 'totalSellers', 0)}
          icon={<LuUsers />}
          variant="secondary"
        />
        <BasicSnapshotCard
          title="Total users"
          value={get(snapshotStatistics, 'totalUsers', 0)}
          icon={<GrGroup />}
          variant="info"
        />
      </Row>
      <Row className="mt-2 dashboard__snapshot-detailed-row">
        <DetailedSnapshotCard
          title="New Buyers"
          value={get(snapshotStatistics, 'newBuyers', 0)}
          variant="success"
          deltaGrowth={deltaGrowth(get(snapshotStatistics, 'newBuyersDelta', 0))}
          delta={mapDelta(get(snapshotStatistics, 'newBuyersDelta', 0))}
          icon={<HiOutlineShoppingCart />}
          showDatePicker
          datePickerStartValue={newBuyersRangeStart}
          datePickerEndValue={newBuyersRangeEnd}
          setDatePickerStartValue={setNewBuyersRangeStart}
          setDatePickerEndValue={setNewBuyersRangeEnd}
          compareStartValue={newBuyersCompareStart}
          compareEndValue={newBuyersCompareEnd}
          setCompareStartValue={setNewBuyersCompareStart}
          setCompareEndValue={setNewBuyersCompareEnd}
          datePickerStartPlaceholder="Today"
          datePickerEndPlaceholder="Yesterday"
        />
        <DetailedSnapshotCard
          title="New Sellers"
          value={get(snapshotStatistics, 'newSellers', 0)}
          variant="secondary"
          deltaGrowth={deltaGrowth(get(snapshotStatistics, 'newSellersDelta', 0))}
          delta={mapDelta(get(snapshotStatistics, 'newSellersDelta', 0))}
          icon={<LuUsers />}
          showDatePicker
          datePickerStartValue={newSellersRangeStart}
          datePickerEndValue={newSellersRangeEnd}
          setDatePickerStartValue={setNewSellersRangeStart}
          setDatePickerEndValue={setNewSellersRangeEnd}
          compareStartValue={newSellersCompareStart}
          compareEndValue={newSellersCompareEnd}
          setCompareStartValue={setNewSellersCompareStart}
          setCompareEndValue={setNewSellersCompareEnd}
          datePickerStartPlaceholder="Today"
          datePickerEndPlaceholder="Yesterday"
        />
        <DetailedSnapshotCard
          title="Vehicles Live"
          value={get(snapshotStatistics, 'totalLiveAuctions', 0)}
          variant="warning"
          icon={<FaWifi />}
          footer={
            <p className="d-flex align-items-center gap-1 mb-0 mt-3 dashboard__detailed-snapshot-card-footer">
              <span className="text-nowrap text-body-color fs-5">
                Ending today: {get(snapshotStatistics, 'liveAuctionsEndingToday', 0)}
              </span>
            </p>
          }
        />
        <DetailedSnapshotCard
          title="Total Number Of Cars Bought"
          value={get(snapshotStatistics, 'boughtVehicles', 0)}
          variant="purple"
          icon={<FaCarSide />}
          showDatePicker
          datePickerStartValue={boughtVehiclesRangeStart}
          datePickerEndValue={boughtVehiclesRangeEnd}
          setDatePickerStartValue={setBoughtVehiclesRangeStart}
          setDatePickerEndValue={setBoughtVehiclesRangeEnd}
          defaultDatePickerStartValue={today}
          defaultDatePickerEndValue={sevenDaysAgo}
          datePickerStartPlaceholder="Last 7 days"
          showDateComparison={false}
        />
      </Row>
    </ComponentCard>
  );
}

export default SnapshotOverviewSection;
